import * as React from "react";
import { useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";
import { CacheProvider } from "@emotion/react";
import { Provider, useStore } from "react-redux";
import { Popout } from "./openPopout";
import { PortalTargetContext } from "../modal/portalTarget";

type Root = ReturnType<typeof createRoot>;

type Props = {
  popout: Popout;
  /** Called when the popout window is closed by the user or the OS. */
  onClose: () => void;
  children: React.ReactNode;
};

/**
 * Renders `children` into an already-opened popout window (see `openPopout`) via a
 * separate React root mounted in that window.
 *
 * A separate root, not a cross-window `createPortal`: React binds event delegation
 * to the root container's document, so a portal into another window renders the DOM
 * but never receives its clicks or inputs. The new root re-provides the store, an
 * Emotion cache targeting the popout head, and `PortalTargetContext` so nested
 * `Modal`s land in the popout.
 *
 * Unmounting (docking back) closes the window; the window closing calls `onClose`.
 */
export function PopoutPortal({ popout, onClose, children }: Props) {
  // From context, not an import — importing the store's module would make this
  // depend on the app entry point.
  const store = useStore();

  // Keep the latest onClose without re-running the lifecycle effect.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const rootRef = useRef<Root | null>(null);

  // Root + window lifecycle: once per popout window.
  useEffect(() => {
    const { win } = popout;
    const root = createRoot(win.document.body);
    rootRef.current = root;

    const handleClose = () => onCloseRef.current();
    win.addEventListener("pagehide", handleClose);
    // Some close paths (popup "X") don't reliably fire pagehide; poll as backup.
    const poll = window.setInterval(() => {
      if (win.closed) onCloseRef.current();
    }, 400);

    // The opener leaving must take the popout with it. Sign-out (including the
    // inactivity auto-logout) navigates away without unmounting React, so the
    // cleanup below never runs and the window is orphaned showing a signed-out
    // session. Both hooks, because neither fires reliably on its own.
    const closeWindow = () => {
      if (!win.closed) win.close();
    };
    window.addEventListener("pagehide", closeWindow);
    window.addEventListener("beforeunload", closeWindow);

    return () => {
      win.removeEventListener("pagehide", handleClose);
      window.removeEventListener("pagehide", closeWindow);
      window.removeEventListener("beforeunload", closeWindow);
      window.clearInterval(poll);
      // Cleared now, not in the timeout: nothing should render into a dying root.
      rootRef.current = null;

      // Deferred: the Dock button lives in this root, so docking arrives mid-render
      // and a synchronous unmount races it. Guarded for the already-closed window.
      window.setTimeout(() => {
        try {
          root.unmount();
        } catch {
          /* nothing left to unmount from */
        }
        if (!win.closed) win.close();
      }, 0);
    };
  }, [popout]);

  // (Re)render into the popout root whenever the content changes.
  useEffect(() => {
    rootRef.current?.render(
      <Provider store={store}>
        <CacheProvider value={popout.cache}>
          <PortalTargetContext.Provider value={popout.win.document}>
            {children}
          </PortalTargetContext.Provider>
        </CacheProvider>
      </Provider>,
    );
  }, [children, popout, store]);

  return null;
}
